import React from 'react';
import { BaseEdge, EdgeLabelRenderer, getBezierPath, type EdgeProps } from 'reactflow';
import SeamButton from './SeamButton';
import type { StoryboardShot, TransitionKind } from '../../services/studioService';

export interface TransitionEdgeData {
  /** Index of the shot this link leads INTO — the transition is stored on the following shot. */
  intoIndex: number;
  current?: TransitionKind;
  onChange?: (t: TransitionKind) => void;
  /** True when the link crosses a scene boundary, which is a cut by definition. */
  acrossScenes?: boolean;
}

/**
 * TRANSITION EDGE — the link between two shots, drawn as a ComfyUI-style bezier and labelled with the cut.
 *
 * This replaces the floating arrow that used to sit between the cards. The difference matters: an arrow
 * parked in the gap is decoration a creator has to guess at, whereas a LINK running port-to-port is the
 * thing every node-editor user already reads as "this feeds that". It also survives reordering for free,
 * because React Flow re-routes an edge from its endpoints rather than from a hardcoded position.
 *
 * A link ACROSS scenes is dashed — a scene change is a harder break than a join inside one continuous scene,
 * and the board should show that difference rather than make the creator remember it.
 */
const TransitionEdge: React.FC<EdgeProps<TransitionEdgeData>> = ({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, style,
}) => {
  const [path, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
    // Gentle, so a link between adjacent cards reads as a short hop rather than a long swooping cable.
    curvature: 0.35,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={{
          // secondary-400/500 reads on both grounds — a single hardcoded stroke disappeared on one of them.
          stroke: 'rgb(161 161 170)',
          strokeWidth: 1.5,
          strokeDasharray: data?.acrossScenes ? '5 4' : undefined,
          ...style,
        }}
      />
      {/* The label is real DOM in the viewport transform, so the chooser is clickable and pans/zooms with
          the board. `nodrag`/`nopan` stop a click on it from panning the canvas underneath. */}
      {data?.onChange && (
        <EdgeLabelRenderer>
          <div
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
            className="nodrag nopan pointer-events-auto absolute"
          >
            <SeamButton
              intoIndex={data.intoIndex}
              current={data.current}
              onChange={data.onChange}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default TransitionEdge;

/** Build the link list from the shots array — one link per consecutive pair, always in play order. */
export const transitionEdges = (
  shots: StoryboardShot[],
  sceneStartsAt: (index: number) => boolean,
  onShotChange?: (index: number, patch: Partial<StoryboardShot>) => void,
) => shots.slice(0, -1).map((_, i) => ({
  id: `link-${i}`,
  source: `shot-${i}`,
  target: `shot-${i + 1}`,
  type: 'transition',
  // Never selectable or deletable: the sequence is not something a creator can unwire. Removing the link
  // between two shots would mean an episode that does not play, which is not a state worth allowing.
  selectable: false,
  deletable: false,
  focusable: false,
  data: {
    intoIndex: i + 1,
    current: shots[i + 1]?.transition,
    acrossScenes: sceneStartsAt(i + 1),
    onChange: onShotChange ? (t: TransitionKind) => onShotChange(i + 1, { transition: t }) : undefined,
  },
}));
